import { ChangeDetectionStrategy, Component, HostBinding, inject, OnInit, signal } from '@angular/core';
import { ICP_EXPIRED_STATUSES } from '@uigy/modules/icps/models/icp.interface';
import { MessageBlockComponent, MessageLink } from '@uigy/shared/components/message-block/message-block.component';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { FeatureFlagDirective } from '@uigy/shared/directives/feature-flag.directive';
import { FeatureFlags } from '@uigy/shared/enums/feature-flags.enum';
import { SessionStorageEnum } from '@uigy/shared/enums/storageKeys.enum';
import { LanguageService } from '@uigy/shared/services/language.service';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { UIButtonComponent } from '@uigy/shared/components/ui-button/ui-button.component';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { HeadingComponent } from '@uigy/shared/components/heading/heading.component';
import { WhatsNewKpiWrapperComponent, } from '@uigy/modules/overview/kpis/kpi-whats-new/whats-new-kpi-wrapper/whats-new-kpi-wrapper.component';
import { RouterLink } from '@angular/router';
import { MainKpiWrapperComponent, } from '@uigy/modules/overview/kpis/kpi-main/main-kpi-wrapper/main-kpi-wrapper.component';
import { CommonModule } from '@angular/common';
import { UiPieChartCardComponent, } from '@uigy/shared/components/ui-pie-chart/ui-pie-chart-card/ui-pie-chart-card.component';
import { PieChartByTypeDataProvider } from '@uigy/modules/overview/services/pieChartByTypeDataProvider.service';
import { PieChartByCriticalityDataProvider, } from '@uigy/modules/overview/services/pieChartByCriticalityDataProvider.service';
import { RiskScoreComponent } from '@uigy/shared/components/risk-score/risk-score.component';
import { TableCardComponent } from '@uigy/shared/components/widgets/table-card/table-card.component';
import { HighestRiskGridSettingsService, } from '@uigy/modules/overview/services/highest-risk-table-grid-settings.service';
import { IemConnectedSitesGridSettingsService, } from '@uigy/modules/overview/services/iem-connected-sites-table-grid-settings.service';
import { IemService } from '@uigy/shared/services/iem.service';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { HOST_ATTR_APP_ID } from '@uigy/shared/directives/app-id/host-attributes.constant';
import Utils from '@uigy/utils';
import { map, take } from 'rxjs/operators';
import { EmPairingStatus } from '@uigy/generated/graphql';

@Component({
  selector: 'app-overview-page',
  imports: [
    UIButtonComponent,
    HeadingComponent,
    WhatsNewKpiWrapperComponent,
    MainKpiWrapperComponent,
    RouterLink,
    RiskScoreComponent,
    CommonModule,
    UiPieChartCardComponent,
    TableCardComponent,
    FeatureFlagDirective,
    MessageBlockComponent,
  ],
  templateUrl: './overview-page.component.html',
  styleUrl: './overview-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewPageComponent implements OnInit {
  protected readonly IconsEnum = IconsEnum;
  public readonly texts = this.languageService.texts.overview;
  public readonly AppIdEnum = AppIdsEnum;
  public readonly generate_report_path = `/${RoutesStatesNamesEnum.report}`;
  public readonly isEm = signal<boolean>(this.iemService.isIEM());
  public readonly tableCardSettings = this.isEm() ? this.iemConnectedSitesGridSettingsService : this.highestRiskGridSettingsService;
  public readonly FeatureFlags = FeatureFlags;
  public showIcpLicenseExpiredMessage = signal(false);
  protected toasterType = ToasterType;
  public dashboardTexts = this.languageService.texts.dashboards;
  public icpsLink: MessageLink = {
    text: this.dashboardTexts.icps_link,
    ref: [RoutesStatesNamesEnum.root, RoutesStatesNamesEnum.icps],
  };

  @HostBinding(HOST_ATTR_APP_ID)
  public readonly hostAppId: string = AppIdsEnum.page_overview;

  constructor(private languageService: LanguageService,
              public pieChartByType: PieChartByTypeDataProvider,
              public pieChartByCriticality: PieChartByCriticalityDataProvider,
              private highestRiskGridSettingsService: HighestRiskGridSettingsService,
              private iemConnectedSitesGridSettingsService: IemConnectedSitesGridSettingsService,
              private iemService: IemService) {

  }

  public ngOnInit(): void {
    if (this.isEm()) {
      this.iemService.getIemPairedIcps().pipe(
        take(1),
        map(pairedIcpsConnection => Utils.stripConnection(pairedIcpsConnection)),
        map(pairedIcps => pairedIcps
          .filter(pairedIcp => pairedIcp.status !== EmPairingStatus.IcpDisconnected)
          .some(pairedIcp => ICP_EXPIRED_STATUSES.includes(pairedIcp.license.status))
        ),
      ).subscribe(hasExpiredIcps => {
        const expiredMessageWasDismissed = sessionStorage.getItem(SessionStorageEnum.icpLicenseExpiredPopup);
        this.showIcpLicenseExpiredMessage.set(hasExpiredIcps && !expiredMessageWasDismissed);
      });
    }
  }

  // UX spec - once dismissed, don't show until logout
  public saveDismissToStorage(): void {
    sessionStorage.setItem(SessionStorageEnum.icpLicenseExpiredPopup, 'dismissed');
  }
}
